import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Lock, Search, Target, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { cn } from '@/lib/cn'
import { useRoles } from '@/features/team/queries'
import { useDirectoryMembers } from '@/features/agents/queries'
import { useAuth } from '@/auth/useAuth'
import type { ChatThreadVisibility } from './types'

export interface VisibilityValue {
  visibility: ChatThreadVisibility
  sharedRoleIds: number[]
  sharedUserIds: number[]
}

interface Props {
  /** Initial state when the picker mounts. */
  initial?: Partial<VisibilityValue>
  /** Called when the user clicks Apply. The parent then makes the API call. */
  onApply: (value: VisibilityValue) => void
  /** Called on Cancel - parent should close the popover. */
  onCancel?: () => void
  /** Show a busy spinner / disable the Apply button while parent saves. */
  busy?: boolean
  /** Apply button label - defaults to "Apply". */
  applyLabel?: string
  /** Hide the Cancel button (useful when the popover already has a close X). */
  hideCancel?: boolean
}

type RoleMode = 'all' | 'specific'

/**
 * Single source of truth for the chat visibility picker.
 *
 * Used in two places:
 *   - the sidebar "+" dropdown (creating a new chat with a chosen visibility)
 *   - the chat header badge (changing an existing chat's visibility)
 *
 * Three top-level options:
 *   - Private        : only the creator can see / open the chat
 *   - Workspace      : every tenant member with ai.chatbot can see it
 *   - Specific roles : creator + a curated subset of one or more roles.
 *                      For each picked role the user chooses either
 *                      "everyone in this role" (role goes to
 *                      sharedRoleIds) or "specific members from this
 *                      role" (their user_ids go to sharedUserIds and
 *                      the role does NOT get the blanket grant).
 */
export function VisibilityPicker({
  initial,
  onApply,
  onCancel,
  busy = false,
  applyLabel = 'Apply',
  hideCancel = false,
}: Props) {
  const rolesQ = useRoles()
  const { user } = useAuth()
  const currentUserId = user?.userId ? Number(user.userId) : null
  const [mode, setMode] = useState<ChatThreadVisibility>(
    initial?.visibility ?? 'private',
  )
  const [openRoles, setOpenRoles] = useState<Set<number>>(new Set())
  // For each selected role: 'all' = grant the whole role; 'specific' =
  // grant only the picked members from that role's roster.
  const [roleSettings, setRoleSettings] = useState<
    Map<number, { roleMode: RoleMode; pickedUserIds: Set<number> }>
  >(new Map())
  const [roleSearch, setRoleSearch] = useState('')
  const [memberSearch, setMemberSearch] = useState<Record<number, string>>({})
  const [error, setError] = useState<string | null>(null)

  // Directory load is required so we can group the existing
  // sharedUserIds by their role on hydration AND filter members per
  // role in the picker. Lazy-load it only when the user is in the
  // shared mode so private/workspace switches stay cheap.
  const directoryQ = useDirectoryMembers(mode === 'shared')

  // Hydrate (or re-hydrate) when `initial` or the directory data changes.
  // We can only group sharedUserIds by their role once the directory has
  // resolved - until then those users live as a pending list and get
  // distributed in a follow-up pass.
  useEffect(() => {
    if (!initial) return
    const v = initial.visibility ?? 'private'
    setMode(v)
    if (v !== 'shared') {
      setRoleSettings(new Map())
      return
    }
    const next = new Map<number, { roleMode: RoleMode; pickedUserIds: Set<number> }>()
    for (const rid of initial.sharedRoleIds ?? []) {
      next.set(rid, { roleMode: 'all', pickedUserIds: new Set() })
    }
    const members = directoryQ.data?.members ?? []
    if (members.length === 0 && (initial.sharedUserIds ?? []).length > 0) {
      // Directory not loaded yet - keep existing role grants but defer
      // user-distribution. The next render with directory data will
      // re-run this effect and place each user under its role.
      setRoleSettings(next)
      return
    }
    const roleByUser = new Map<number, number>()
    for (const m of members) {
      if (m.role_id != null) roleByUser.set(m.user_id, m.role_id)
    }
    for (const uid of initial.sharedUserIds ?? []) {
      const rid = roleByUser.get(uid)
      if (rid == null) continue // user no longer in tenant - drop silently
      const slot = next.get(rid)
      if (slot) {
        // Role already has 'all' from sharedRoleIds - leave it; the user
        // is implicitly covered. The picker won't reduce coverage by
        // forcing 'specific' over a wider 'all' grant.
        continue
      }
      next.set(rid, { roleMode: 'specific', pickedUserIds: new Set([uid]) })
    }
    // Merge follow-up users into existing 'specific' slots
    for (const uid of initial.sharedUserIds ?? []) {
      const rid = roleByUser.get(uid)
      if (rid == null) continue
      const slot = next.get(rid)
      if (slot && slot.roleMode === 'specific') slot.pickedUserIds.add(uid)
    }
    setRoleSettings(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initial?.visibility,
    initial?.sharedRoleIds?.join(','),
    initial?.sharedUserIds?.join(','),
    directoryQ.data,
  ])

  const allRoles = useMemo(() => rolesQ.data?.roles ?? [], [rolesQ.data])
  const filteredRoles = useMemo(() => {
    const q = roleSearch.trim().toLowerCase()
    if (!q) return allRoles
    return allRoles.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.description ?? '').toLowerCase().includes(q),
    )
  }, [allRoles, roleSearch])

  const membersByRole = useMemo(() => {
    const out = new Map<number, Array<{ user_id: number; name?: string | null; email: string }>>()
    for (const m of directoryQ.data?.members ?? []) {
      if (m.user_id === currentUserId) continue
      if (m.role_id == null) continue
      const list = out.get(m.role_id) ?? []
      list.push({ user_id: m.user_id, name: m.name, email: m.email })
      out.set(m.role_id, list)
    }
    return out
  }, [directoryQ.data, currentUserId])

  const toggleRole = (id: number) => {
    setRoleSettings((prev) => {
      const next = new Map(prev)
      if (next.has(id)) next.delete(id)
      else next.set(id, { roleMode: 'all', pickedUserIds: new Set() })
      return next
    })
    setOpenRoles((prev) => {
      const next = new Set(prev)
      // Auto-expand a freshly checked role so the All/Specific control
      // is immediately visible without an extra click.
      if (!roleSettings.has(id)) next.add(id)
      else next.delete(id)
      return next
    })
  }
  const setRoleMode = (id: number, m: RoleMode) =>
    setRoleSettings((prev) => {
      const next = new Map(prev)
      const cur = next.get(id)
      if (!cur) return prev
      next.set(id, {
        roleMode: m,
        pickedUserIds: m === 'specific' ? cur.pickedUserIds : new Set(),
      })
      return next
    })
  const toggleMember = (roleId: number, uid: number) =>
    setRoleSettings((prev) => {
      const next = new Map(prev)
      const cur = next.get(roleId)
      if (!cur) return prev
      const ids = new Set(cur.pickedUserIds)
      if (ids.has(uid)) ids.delete(uid)
      else ids.add(uid)
      next.set(roleId, { ...cur, pickedUserIds: ids })
      return next
    })
  const toggleOpen = (id: number) =>
    setOpenRoles((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const totalRolesPicked = roleSettings.size
  const totalMembersPicked = useMemo(() => {
    let n = 0
    for (const s of roleSettings.values()) {
      if (s.roleMode === 'specific') n += s.pickedUserIds.size
    }
    return n
  }, [roleSettings])

  const submit = () => {
    setError(null)
    if (mode === 'shared') {
      if (totalRolesPicked === 0) {
        setError('Pick at least one role, or choose Private / Workspace.')
        return
      }
      for (const [roleId, s] of roleSettings) {
        if (s.roleMode === 'specific' && s.pickedUserIds.size === 0) {
          const role = allRoles.find((r) => r.id === roleId)
          setError(
            `Pick at least one member from "${role?.name ?? 'role'}" or switch it to "All members".`,
          )
          return
        }
      }
    }
    const sharedRoleIds: number[] = []
    const sharedUserIds: number[] = []
    if (mode === 'shared') {
      for (const [roleId, s] of roleSettings) {
        if (s.roleMode === 'all') sharedRoleIds.push(roleId)
        else for (const u of s.pickedUserIds) sharedUserIds.push(u)
      }
    }
    onApply({ visibility: mode, sharedRoleIds, sharedUserIds })
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-2">
        <PickOption
          icon={<Lock className="size-4" aria-hidden />}
          title="Private"
          description="Only you can open this chat."
          selected={mode === 'private'}
          onClick={() => setMode('private')}
        />
        <PickOption
          icon={<Users className="size-4" aria-hidden />}
          title="Workspace"
          description="Every member of this workspace can read it."
          selected={mode === 'tenant'}
          onClick={() => setMode('tenant')}
        />
        <PickOption
          icon={<Target className="size-4" aria-hidden />}
          title="Specific roles"
          description="Pick roles, then everyone or specific members from each."
          selected={mode === 'shared'}
          onClick={() => setMode('shared')}
        />
      </div>

      {mode === 'shared' ? (
        <div className="space-y-2 rounded-md border bg-muted/30 p-2">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              value={roleSearch}
              onChange={(e) => setRoleSearch(e.currentTarget.value)}
              placeholder="Search roles…"
              className="h-8 pl-7 text-xs"
            />
          </div>
          <div className="max-h-72 overflow-auto rounded-md border bg-card">
            {rolesQ.isLoading ? (
              <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                Loading roles…
              </div>
            ) : filteredRoles.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                {allRoles.length === 0
                  ? 'No roles in this workspace.'
                  : 'No roles match your search.'}
              </div>
            ) : (
              filteredRoles.map((r) => {
                const cfg = roleSettings.get(r.id)
                const checked = !!cfg
                const expanded = openRoles.has(r.id)
                const roleMembers = membersByRole.get(r.id) ?? []
                const memberQ = (memberSearch[r.id] ?? '').trim().toLowerCase()
                const visibleMembers = memberQ
                  ? roleMembers.filter(
                      (m) =>
                        (m.name ?? '').toLowerCase().includes(memberQ) ||
                        m.email.toLowerCase().includes(memberQ),
                    )
                  : roleMembers
                return (
                  <div key={r.id} className="border-b last:border-b-0">
                    <div className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-xs hover:bg-accent">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleRole(r.id)}
                        aria-label={`Share with ${r.name}`}
                      />
                      <button
                        type="button"
                        onClick={() => (checked ? toggleOpen(r.id) : toggleRole(r.id))}
                        className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                      >
                        {checked ? (
                          expanded ? (
                            <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                          ) : (
                            <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                          )
                        ) : (
                          <span className="inline-block size-3.5 shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate font-medium">{r.name}</span>
                            {r.is_system ? (
                              <Badge variant="secondary" className="text-[9px]">
                                system
                              </Badge>
                            ) : null}
                            {checked && cfg?.roleMode === 'specific' ? (
                              <Badge variant="info" className="text-[9px]">
                                {cfg.pickedUserIds.size} member
                                {cfg.pickedUserIds.size === 1 ? '' : 's'}
                              </Badge>
                            ) : null}
                          </div>
                          {r.description ? (
                            <div className="truncate text-[11px] text-muted-foreground">
                              {r.description}
                            </div>
                          ) : null}
                        </div>
                      </button>
                    </div>
                    {checked && expanded ? (
                      <div className="space-y-2 border-t bg-muted/20 px-2 py-2 text-xs">
                        <div className="flex items-center gap-3">
                          <label className="flex cursor-pointer items-center gap-1.5">
                            <input
                              type="radio"
                              className="size-3"
                              checked={cfg?.roleMode === 'all'}
                              onChange={() => setRoleMode(r.id, 'all')}
                            />
                            All members
                          </label>
                          <label className="flex cursor-pointer items-center gap-1.5">
                            <input
                              type="radio"
                              className="size-3"
                              checked={cfg?.roleMode === 'specific'}
                              onChange={() => setRoleMode(r.id, 'specific')}
                              disabled={roleMembers.length === 0}
                            />
                            Specific members
                          </label>
                        </div>
                        {cfg?.roleMode === 'specific' ? (
                          <>
                            <Input
                              value={memberSearch[r.id] ?? ''}
                              onChange={(e) =>
                                setMemberSearch((prev) => ({
                                  ...prev,
                                  [r.id]: e.currentTarget.value,
                                }))
                              }
                              placeholder="Search members…"
                              className="h-7 text-xs"
                            />
                            <div className="max-h-40 overflow-auto rounded border bg-card">
                              {directoryQ.isLoading ? (
                                <div className="px-2 py-3 text-center text-[11px] text-muted-foreground">
                                  Loading members…
                                </div>
                              ) : visibleMembers.length === 0 ? (
                                <div className="px-2 py-3 text-center text-[11px] text-muted-foreground">
                                  {roleMembers.length === 0
                                    ? 'No active members in this role.'
                                    : 'No members match your search.'}
                                </div>
                              ) : (
                                visibleMembers.map((m) => {
                                  const checkedMember =
                                    cfg.pickedUserIds.has(m.user_id)
                                  return (
                                    <label
                                      key={m.user_id}
                                      className="flex cursor-pointer items-center gap-2 border-b px-2 py-1 last:border-b-0 hover:bg-accent"
                                    >
                                      <Checkbox
                                        checked={checkedMember}
                                        onCheckedChange={() =>
                                          toggleMember(r.id, m.user_id)
                                        }
                                      />
                                      <div className="min-w-0 flex-1">
                                        <div className="truncate font-medium">
                                          {m.name || m.email}
                                        </div>
                                        {m.name ? (
                                          <div className="truncate text-[10px] text-muted-foreground">
                                            {m.email}
                                          </div>
                                        ) : null}
                                      </div>
                                    </label>
                                  )
                                })
                              )}
                            </div>
                          </>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                )
              })
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">
            {totalRolesPicked} role{totalRolesPicked === 1 ? '' : 's'} ·{' '}
            {totalMembersPicked} member{totalMembersPicked === 1 ? '' : 's'} picked.
          </p>
        </div>
      ) : null}

      {error ? (
        <Alert variant="destructive">
          <AlertDescription className="text-xs">{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className="flex justify-end gap-2 pt-1">
        {hideCancel ? null : (
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        )}
        <Button size="sm" onClick={submit} disabled={busy}>
          {busy ? 'Saving…' : applyLabel}
        </Button>
      </div>
    </div>
  )
}

function PickOption({
  icon,
  title,
  description,
  selected,
  onClick,
}: {
  icon: React.ReactNode
  title: string
  description: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      onClick={onClick}
      className={cn(
        // whitespace-normal overrides the default Button whitespace-nowrap
        // so the longer descriptions ("Pick roles, then everyone …")
        // wrap inside the popover instead of overflowing.
        'h-auto w-full justify-start gap-2 whitespace-normal rounded-md border p-2 text-left transition',
        selected
          ? 'border-primary bg-primary/10 hover:bg-primary/15'
          : 'border-input hover:border-primary/40',
      )}
    >
      <span
        className={cn(
          'mt-0.5 shrink-0',
          selected ? 'text-primary' : 'text-muted-foreground',
        )}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-semibold">{title}</span>
        <span className="block text-[11px] font-normal text-muted-foreground">
          {description}
        </span>
      </span>
    </Button>
  )
}
